import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, 
  CheckSquare, 
  Clock, 
  Plus, 
  MoreVertical,
  ArrowRight,
  Zap
} from 'lucide-react';
import { format, isToday, isBefore, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useEvents } from '@/hooks/useEvents';
import { useTasks } from '@/hooks/useTasks';
import { useFamily } from '@/hooks/useFamily';
import { EventWithTasks } from './EventWithTasks';
import { useModalNavigation } from '@/hooks/useModalNavigation';
import { QuickActions } from './QuickActions';
import { PriorityIndicator } from './PriorityIndicator';

interface TimelineItem {
  id: string;
  type: 'event' | 'task';
  title: string;
  time?: string;
  date: Date;
  priority: number;
  isOverdue?: boolean;
  status?: string;
  linkedPersons?: string[];
  assignedTo?: string[];
  event_id?: string;
}

export function TodayTimeline() {
  const [showQuickActions, setShowQuickActions] = useState(false);
  const { family } = useFamily();
  const { events } = useEvents(family?.id);
  const { tasks } = useTasks(family?.id);
  const { openModal } = useModalNavigation();

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

  // Collect today's items
  const todayItems: TimelineItem[] = [];

  // Add today's events
  if (events) {
    events.forEach(event => {
      const eventDate = new Date(event.start_date);
      if (isToday(eventDate)) {
        todayItems.push({
          id: event.id,
          type: 'event',
          title: event.title,
          time: format(eventDate, 'HH:mm'),
          date: eventDate,
          priority: calculateEventPriority(event, eventDate),
          linkedPersons: event.linked_persons || []
        });
      }
    });
  }

  // Add today's tasks
  if (tasks) {
    tasks.forEach(task => {
      if (task.due_date && task.status !== 'completed') {
        const taskDate = new Date(task.due_date + 'T00:00:00');
        if (isToday(taskDate)) {
          todayItems.push({
            id: task.id,
            type: 'task',
            title: task.title,
            date: taskDate,
            priority: calculateTaskPriority(task, taskDate),
            isOverdue: isBefore(taskDate, todayStart),
            status: task.status,
            assignedTo: task.assigned_to_list || [task.assigned_to],
            event_id: task.event_id
          });
        }
      }
    });
  }

  // Group items: events with their related tasks
  const groupedItems: { event: TimelineItem; tasks: TimelineItem[] }[] = [];
  const independentTasks: TimelineItem[] = [];

  // Separate events and tasks
  const todayEvents = todayItems.filter(item => item.type === 'event');
  const todayTasks = todayItems.filter(item => item.type === 'task');

  // Group tasks under their events (include tasks linked to the event even without today's due_date)
  todayEvents.forEach(event => {
    const relatedTasks = (tasks || [])
      .filter(t => t.event_id === event.id && t.status !== 'completed')
      .map((t) => {
        const taskDate = t.due_date ? new Date(t.due_date + 'T00:00:00') : event.date;
        return {
          id: t.id,
          type: 'task',
          title: t.title,
          date: taskDate,
          priority: calculateTaskPriority(t, taskDate),
          isOverdue: t.due_date ? isBefore(taskDate, todayStart) : false,
          status: t.status,
          assignedTo: t.assigned_to_list || [t.assigned_to],
          event_id: t.event_id
        } as TimelineItem;
      });
    groupedItems.push({ event, tasks: relatedTasks });
  });

  // Add independent tasks (not linked to any event)
  todayTasks.forEach(task => {
    if (!task.event_id || !todayEvents.find(event => event.id === task.event_id)) {
      independentTasks.push(task);
    }
  });

  // Sort grouped items by event time, then by priority
  groupedItems.sort((a, b) => {
    if (a.event.time && b.event.time) {
      return a.event.time.localeCompare(b.event.time);
    }
    if (a.event.time && !b.event.time) return -1;
    if (!a.event.time && b.event.time) return 1;
    return b.event.priority - a.event.priority;
  });

  // Sort independent tasks by priority
  independentTasks.sort((a, b) => b.priority - a.priority);

  const allItems = [...todayItems];
  const highPriorityCount = allItems.filter(item => item.priority >= 3).length;
  const overdueCount = allItems.filter(item => item.isOverdue).length;

  if (allItems.length === 0) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <h2 className="text-lg font-semibold">Aujourd'hui</h2>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowQuickActions(!showQuickActions)}
            className="h-8 w-8 p-0"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="text-center py-8">
          <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">Rien de prévu aujourd'hui</p>
          <p className="text-sm text-muted-foreground">Profitez de cette journée libre !</p>
        </div>
        {showQuickActions && (
          <QuickActions onClose={() => setShowQuickActions(false)} />
        )}
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          <h2 className="text-lg font-semibold">Aujourd'hui</h2>
          <div className="flex gap-1">
            {overdueCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {overdueCount} en retard
              </Badge>
            )}
            {highPriorityCount > 0 && (
              <Badge variant="outline" className="text-xs border-orange-300 text-orange-700">
                <Zap className="h-3 w-3 mr-1" />
                {highPriorityCount} priorité
              </Badge>
            )}
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowQuickActions(!showQuickActions)}
          className="h-8 w-8 p-0"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div className="space-y-3">
        {/* Timeline */}
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 top-4 bottom-4 w-0.5 bg-gradient-to-b from-primary/20 via-primary/40 to-primary/20" />
          
          {/* Events with their tasks */}
          {groupedItems.map(({ event, tasks }, index) => (
            <div key={event.id} className="pb-4 last:pb-0">
              <EventWithTasks 
                event={event}
                relatedTasks={tasks}
                isTimeline={true}
                showExpandToggle={false}
              />
            </div>
          ))}

          {/* Independent tasks */}
          {independentTasks.map((item) => (
            <div key={item.id} className="relative flex items-center gap-4 pb-4 last:pb-0">
              {/* Timeline dot */}
              <div className={`relative z-10 w-3 h-3 rounded-full border-2 ${
                item.isOverdue 
                  ? 'bg-red-500 border-red-200'
                  : 'bg-green-500 border-green-200'
              }`} />
              
              {/* Item content */}
              <div className={`flex-1 p-3 rounded-lg border transition-all cursor-pointer ${
                item.isOverdue 
                  ? 'bg-red-50 border-red-200 hover:bg-red-100' 
                  : item.priority >= 3 
                    ? 'bg-orange-50 border-orange-200 hover:bg-orange-100'
                    : 'bg-panel border-panel hover:bg-panel-hover'
              }`}
               onClick={() => openModal('viewTask', { id: item.id })}>
                 <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="p-1.5 rounded-full bg-green-100 text-green-600">
                      <CheckSquare className="h-3 w-3" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-sm truncate">{item.title}</h3>
                        <PriorityIndicator priority={item.priority} size="sm" />
                      </div>
                    </div>
                  </div>
                  
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                    <ArrowRight className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {showQuickActions && (
          <QuickActions onClose={() => setShowQuickActions(false)} />
        )}
      </div>
    </div>
  );
}

function calculateEventPriority(event: any, eventDate: Date): number {
  let priority = 1;
  
  // More attendees = higher priority
  if (event.linked_persons?.length > 3) priority += 2;
  else if (event.linked_persons?.length > 1) priority += 1;
  
  // Location makes it more important
  if (event.location) priority += 1;
  
  // Recurring events are more important
  if (event.recurrence_type !== 'none') priority += 1;
  
  return Math.min(priority, 5);
}

function calculateTaskPriority(task: any, taskDate: Date): number {
  let priority = 1;
  
  // Overdue tasks get max priority
  if (isBefore(taskDate, new Date())) return 5;
  
  // Multiple assignees = higher priority
  if (task.assigned_to_list?.length > 1) priority += 1;
  
  // Task complexity/description suggests importance
  if (task.description && task.description.length > 50) priority += 1;
  
  return Math.min(priority, 5);
}